/**
 * QuickInterimForm - Formulaire simplifié de création d'intérimaire
 * Avec recherche autocomplete pour éviter les doublons
 *
 * Fonctionnement :
 * - En tapant le nom, recherche les intérimaires existants
 * - Clic sur suggestion → sélectionne l'existant (onSelectExisting)
 * - Continuer à taper → créer un nouveau (onSuccess)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_ENDPOINTS } from '../utils/apiConfig';

const QuickInterimForm = ({
  onClose,
  onSuccess,           // Appelé lors de la création d'un nouveau
  onSelectExisting,    // Appelé lors de la sélection d'un existant
  pendingReview = false,
  prestationDayId = null  // Pour exclure les déjà affectés
}) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    agency_id: ''
  });
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lookupData, setLookupData] = useState({
    interimRoleId: null,
    interimStatusId: null
  });

  // Autocomplete states
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedExisting, setSelectedExisting] = useState(null);
  const searchTimeoutRef = useRef(null);
  const nameInputRef = useRef(null);

  // Charger les agences et les données lookup
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');

        // Charger les agences d'intérim
        const agenciesRes = await fetch(API_ENDPOINTS.CLIENTS_AGENCIES, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const agenciesData = await agenciesRes.json();
        if (agenciesData.success) {
          setAgencies(agenciesData.data || []);
        }

        // Charger les rôles pour trouver "Employés"
        const rolesRes = await fetch(API_ENDPOINTS.USERS_LOOKUP_ROLES, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const rolesData = await rolesRes.json();

        // Charger les statuts pour trouver "Interim"
        const statusesRes = await fetch(API_ENDPOINTS.USERS_LOOKUP_EMPLOYEE_STATUSES, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const statusesData = await statusesRes.json();

        if (rolesData.success && statusesData.success) {
          const employeRole = rolesData.data.find(r => r.name === 'Employés');
          const interimStatus = statusesData.data.find(s => s.name === 'Interim' || s.is_temporary);

          setLookupData({
            interimRoleId: employeRole?.id || null,
            interimStatusId: interimStatus?.id || null
          });
        }
      } catch (err) {
        console.error('Erreur chargement données:', err);
        setError('Erreur lors du chargement des données');
      }
    };

    fetchData();
  }, []);

  // Recherche d'intérimaires existants (debounced)
  const searchInterims = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setSearchLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_ENDPOINTS.USERS_SEARCH_INTERIMS}?q=${encodeURIComponent(searchTerm)}&limit=8`;
      if (prestationDayId) {
        url += `&exclude_prestation_day=${prestationDayId}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setSuggestions(data.data || []);
        setShowSuggestions(data.data?.length > 0);
      }
    } catch (err) {
      console.error('Erreur recherche intérimaires:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [prestationDayId]);

  // Gérer le changement de nom avec recherche debounced
  const handleNameChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSelectedExisting(null); // Reset sélection si on modifie

    // Recherche combinée prénom + nom
    const searchTerm = name === 'last_name'
      ? `${formData.first_name} ${value}`.trim()
      : `${value} ${formData.last_name}`.trim();

    // Debounce la recherche
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchInterims(searchTerm);
    }, 300);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Sélectionner un intérimaire existant
  const handleSelectExisting = (interim) => {
    setSelectedExisting(interim);
    setShowSuggestions(false);
    setFormData({
      first_name: interim.first_name,
      last_name: interim.last_name,
      phone: interim.phone || '',
      email: interim.email || '',
      agency_id: ''
    });
  };

  // Confirmer l'ajout de l'existant
  const handleConfirmExisting = () => {
    if (selectedExisting && onSelectExisting) {
      onSelectExisting(selectedExisting);
      onClose && onClose();
    }
  };

  // Annuler la sélection et créer un nouveau
  const handleCreateNew = () => {
    setSelectedExisting(null);
    // Focus sur le champ prénom
    nameInputRef.current?.focus();
  };

  const generateEmail = () => {
    if (formData.first_name && formData.last_name) {
      const cleanFirst = formData.first_name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '');
      const cleanLast = formData.last_name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '');
      return `${cleanFirst}.${cleanLast}.interim@temp.local`;
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Si un existant est sélectionné, utiliser handleConfirmExisting
    if (selectedExisting) {
      handleConfirmExisting();
      return;
    }

    // Validations pour création
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setError('Prénom et nom sont obligatoires');
      return;
    }

    if (!lookupData.interimRoleId || !lookupData.interimStatusId) {
      setError('Configuration manquante: rôle ou statut intérim non trouvé');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const email = formData.email.trim() || generateEmail();

      // Générer un mot de passe aléatoire (l'intérimaire n'aura pas besoin de se connecter)
      const randomPassword = Math.random().toString(36).slice(-10) + 'A1!';

      const response = await fetch(API_ENDPOINTS.USERS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: email,
          phone: formData.phone.trim() || null,
          password: randomPassword,
          role_id: lookupData.interimRoleId,
          employee_status_id: lookupData.interimStatusId,
          agency_id: formData.agency_id || null,
          receive_notifications: false,
          send_activation_email: false,
          pending_review: pendingReview
        })
      });

      const data = await response.json();

      if (data.success) {
        onSuccess && onSuccess(data.data);
        onClose && onClose();
      } else {
        setError(data.error || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('Erreur création intérimaire:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    fontSize: '16px',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#333',
    fontSize: '14px'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '450px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid #eee',
          background: selectedExisting
            ? 'linear-gradient(135deg, #2e7d32, #43a047)'
            : 'linear-gradient(135deg, #6f42c1, #9c27b0)',
          borderRadius: '20px 20px 0 0',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                {selectedExisting ? '✓ Intérimaire trouvé' : '➕ Ajouter un intérimaire'}
              </h2>
              <p style={{ margin: '8px 0 0', fontSize: '14px', opacity: 0.9 }}>
                {selectedExisting ? 'Cliquez sur Ajouter pour confirmer' : 'Recherchez ou créez'}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                fontSize: '20px',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Contenu si existant sélectionné */}
        {selectedExisting ? (
          <div style={{ padding: '24px' }}>
            <div style={{
              background: '#e8f5e9',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '20px',
              border: '2px solid #a5d6a7'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px' }}>
                {selectedExisting.full_name}
              </div>
              {selectedExisting.agency_name && (
                <div style={{ color: '#2e7d32', marginBottom: '8px' }}>
                  🏢 {selectedExisting.agency_code ? `[${selectedExisting.agency_code}] ` : ''}{selectedExisting.agency_name}
                </div>
              )}
              {selectedExisting.phone && (
                <div style={{ color: '#666', marginBottom: '8px' }}>
                  📞 {selectedExisting.phone}
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                {selectedExisting.has_truck_license && (
                  <span style={badgeStyle} title="Permis PL">🚛</span>
                )}
                {selectedExisting.has_forklift_license && (
                  <span style={badgeStyle} title="Cariste">🏗️</span>
                )}
                {selectedExisting.has_sst && (
                  <span style={badgeStyle} title="SST">🩺</span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={handleCreateNew}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: '#f5f5f5',
                  color: '#333',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Créer un nouveau
              </button>
              <button
                type="button"
                onClick={handleConfirmExisting}
                style={{
                  flex: 2,
                  padding: '14px',
                  background: 'linear-gradient(135deg, #2e7d32, #43a047)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span>✓</span>
                Ajouter cet intérimaire
              </button>
            </div>
          </div>
        ) : (
          /* Formulaire de recherche/création */
          <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
            {error && (
              <div style={{
                background: '#ffebee',
                color: '#c62828',
                padding: '12px 16px',
                borderRadius: '12px',
                marginBottom: '20px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>⚠️</span>
                {error}
              </div>
            )}

            {/* Nom (avec autocomplete) */}
            <div style={{ marginBottom: '20px', position: 'relative' }}>
              <label style={labelStyle}>
                Nom <span style={{ color: '#e53935' }}>*</span>
                {searchLoading && <span style={{ marginLeft: '8px', color: '#888' }}>🔍</span>}
              </label>
              <input
                ref={nameInputRef}
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleNameChange}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="Tapez pour rechercher..."
                style={{
                  ...inputStyle,
                  borderColor: showSuggestions ? '#9c27b0' : '#e0e0e0'
                }}
                autoFocus
                autoComplete="off"
              />

              {/* Dropdown suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  marginTop: '4px',
                  zIndex: 100,
                  maxHeight: '250px',
                  overflow: 'auto',
                  border: '2px solid #9c27b0'
                }}>
                  <div style={{ padding: '8px 12px', background: '#f3e5f5', color: '#7b1fa2', fontSize: '12px', fontWeight: '600' }}>
                    💡 Intérimaires existants
                  </div>
                  {suggestions.map((interim) => (
                    <div
                      key={interim.id}
                      onClick={() => handleSelectExisting(interim)}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.background = 'white'}
                    >
                      <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                        {interim.full_name}
                      </div>
                      <div style={{ fontSize: '13px', color: '#666', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        {interim.agency_name && (
                          <span>🏢 {interim.agency_name}</span>
                        )}
                        {interim.phone && (
                          <span>📞 {interim.phone}</span>
                        )}
                      </div>
                    </div>
                  ))}
                  <div
                    onClick={() => setShowSuggestions(false)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      background: '#fafafa',
                      color: '#6f42c1',
                      fontWeight: '600',
                      textAlign: 'center',
                      borderTop: '2px solid #e0e0e0'
                    }}
                  >
                    ➕ Créer "{formData.last_name}" comme nouveau
                  </div>
                </div>
              )}
            </div>

            {/* Prénom */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                Prénom <span style={{ color: '#e53935' }}>*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleNameChange}
                placeholder="Prénom de l'intérimaire"
                style={inputStyle}
                autoComplete="off"
              />
            </div>

            {/* Téléphone */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                Téléphone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="06 XX XX XX XX"
                style={inputStyle}
              />
            </div>

            {/* Agence d'intérim (optionnel) */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>
                Agence d'intérim <span style={{ color: '#888', fontWeight: 'normal' }}>(optionnel)</span>
              </label>
              <select
                name="agency_id"
                value={formData.agency_id}
                onChange={handleChange}
                style={{
                  ...inputStyle,
                  cursor: 'pointer',
                  appearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'currentColor\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 12px center',
                  backgroundSize: '16px'
                }}
              >
                <option value="">-- Non renseignée --</option>
                {agencies.map(agency => (
                  <option key={agency.id} value={agency.id}>
                    {agency.code ? `[${agency.code}] ` : ''}{agency.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Email (optionnel) - masqué pour simplifier */}
            <input type="hidden" name="email" value={formData.email} />

            {/* Boutons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: '#f5f5f5',
                  color: '#333',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 2,
                  padding: '14px',
                  background: loading ? '#ccc' : 'linear-gradient(135deg, #6f42c1, #9c27b0)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner" style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Création...
                  </>
                ) : (
                  <>
                    <span>✓</span>
                    Créer l'intérimaire
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

const badgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  background: 'white',
  borderRadius: '8px',
  fontSize: '18px',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
};

export default QuickInterimForm;
